import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom'
import { makeStyles } from "@material-ui/core/styles";
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import FilterListIcon from '@material-ui/icons/FilterList';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import Collapse from '@material-ui/core/Collapse';

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import Badge from '@material-ui/core/Badge';

import { getImageUrl, hasValidImage } from './utils';
import { displaySeriesTitle } from './services/seriesService';
import Pagination from './Pagination';


const useStyles = makeStyles(theme => ({
    webPlant: {
        padding: '7px 20px 0px 10px',
        display: 'inline-flex',
        [theme.breakpoints.down('xs')]: {
            padding: '7px 8px 0px 8px'
        }
    },
    separator: {
        paddingTop: '10px',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        [theme.breakpoints.down('xs')]: {
            justifyContent: 'center'
        }
    },
    seriesCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100px',
        textAlign: 'center'
    },
    seriesImage: {
        width: '100px',
        height: '150px',
        objectFit: 'cover',
        display: 'block'
    },
    seriesPlaceholder: {
        width: '100px',
        height: '150px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f0f0f0',
        border: '1px solid #ccc',
        textAlign: 'center',
        padding: '8px',
        boxSizing: 'border-box',
        fontSize: '0.75em',
        wordWrap: 'break-word',
        overflow: 'hidden'
    },
    seriesTitle: {
        width: '100px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '0.8em',
        marginTop: '4px'
    },
    filterButton: {
        backgroundColor: theme.palette.background.paper,
        border: '1px solid',
        borderColor: theme.palette.divider,
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    },
    fabContainer: {
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 1000,
        [theme.breakpoints.down('xs')]: {
            bottom: '16px',
            right: '16px'
        }
    },
    fab: {
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingRight: theme.spacing(1)
    },
    dialogContentMobile: {
        [theme.breakpoints.down('xs')]: {
            paddingTop: theme.spacing(1)
        }
    },
    searchField: {
        marginBottom: theme.spacing(2)
    },
    filterContainer: {
        padding: '12px 16px',
        marginBottom: '12px',
        backgroundColor: theme.palette.background.paper,
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    filterInput: {
        width: '100%',
        maxWidth: '300px'
    }
}));
function ComicSeries(props) {
    const [seriesSearch, setSeriesSearch] = useState('');
    const [seriesId, setSeriesId] = useState('');
    const [startYear, setStartYear] = useState('');
    
    // Search dialog state
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    
    // Filter state
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterText, setFilterText] = useState('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    
    const classes = useStyles();
    
    // Filter series by text
    const filteredSeries = useMemo(() => {
        if (!filterText.trim()) return props.series;
        
        const searchLower = filterText.toLowerCase();
        return props.series.filter(series => 
            displaySeriesTitle(series.title)?.toLowerCase().includes(searchLower)
        );
    }, [props.series, filterText]);
    
    // Sort series
    const sortedSeries = useMemo(() => {
        return [...filteredSeries].sort((a, b) =>
            b.startYear - a.startYear ||
            b.modified - a.modified
        );
    }, [filteredSeries]);
    
    // Apply pagination
    const paginatedSeries = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return sortedSeries.slice(startIndex, startIndex + pageSize);
    }, [sortedSeries, currentPage, pageSize]);
    
    // Reset pagination when series list changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [props.series.length, filterText]);
    
    // Shared pagination props to avoid duplication
    const paginationProps = {
        currentPage,
        totalItems: filteredSeries.length,
        pageSize,
        onPageChange: setCurrentPage,
        onPageSizeChange: setPageSize
    };
    
    const handleSearchSubmit = () => {
        props.addSeries(props.hero, seriesSearch, seriesId.trim(), startYear);
        setSearchDialogOpen(false);
        setSeriesSearch('');
        setSeriesId('');
        setStartYear('');
    };
    
    // Filter button to pass to pagination
    const filterButtonElement = (
        <IconButton 
            className={classes.filterButton}
            onClick={() => setFilterOpen(!filterOpen)}
            aria-label="Filter"
            size="small"
        >
            <FilterListIcon />
        </IconButton>
    );

    return (
        <div>
            {props.series.length > 0 && (
                <Pagination {...paginationProps} filterButton={filterButtonElement} />
            )}
            
            {/* Filter collapse section */}
            <Collapse in={filterOpen}>
                <div className={classes.filterContainer}>
                    <TextField
                        className={classes.filterInput}
                        variant="outlined"
                        size="small"
                        placeholder="Filter series..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: filterText && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setFilterText('')}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </div>
            </Collapse>
            
            <div className={classes.separator}>
                {paginatedSeries.map(card => <SeriesLink key={card.id} card={card} hero={props.hero} setCurrentSeries={props.setCurrentSeries} editMode={props.editMode} />)}
            </div>
            
            {props.series.length > pageSize && (
                <Pagination {...paginationProps} />
            )}
            
            {/* Add FAB button */}
            <div className={classes.fabContainer}>
                <Fab 
                    color="primary" 
                    className={classes.fab}
                    onClick={() => setSearchDialogOpen(true)}
                    aria-label="Search series"
                >
                    <AddIcon />
                </Fab>
            </div>
            
            {/* Search Dialog */}
            <Dialog 
                open={searchDialogOpen} 
                onClose={() => setSearchDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    style: {
                        position: 'fixed',
                        top: '10%',
                        margin: 0
                    }
                }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <span>Search Comic Vine</span>
                    <IconButton size="small" onClick={() => setSearchDialogOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        variant="outlined"
                        label="Series Name"
                        placeholder="Enter series name..."
                        value={seriesSearch}
                        onChange={(e) => setSeriesSearch(e.target.value)}
                        className={classes.searchField}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearchSubmit();
                            }
                        }}
                    />
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Series ID (Comic Vine)"
                        placeholder="e.g., 17376"
                        value={seriesId}
                        onChange={(e) => setSeriesId(e.target.value)}
                        className={classes.searchField}
                        helperText="Enter a Comic Vine volume ID to add directly without searching"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearchSubmit();
                            }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        title="Search Comic Vine for series IDs"
                                        onClick={() => window.open('https://comicvine.gamespot.com/new-comics/', '_blank', 'noopener,noreferrer')}
                                    >
                                        <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Start Year (optional, for filtering)"
                        placeholder="e.g., 2020"
                        value={startYear}
                        onChange={(e) => setStartYear(e.target.value)}
                        helperText="Results will be filtered by this year (not sent to Comic Vine)"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearchSubmit();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSearchDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSearchSubmit} 
                        color="primary" 
                        variant="contained"
                        disabled={!seriesSearch.trim() && !seriesId.trim()}
                    >
                        Search
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );

};

// function Series(props){
//    return <div>{props.card.title}</div>
// }

const SeriesLink = (props) => {
    const classes = useStyles();
    // Use actualAvailable if set (non-variant count), otherwise fall back to comics.available from API
    const totalAvailable = props.card.actualAvailable !== undefined && props.card.actualAvailable > 0 
        ? props.card.actualAvailable 
        : props.card.comics.available;
    
    const hasImage = hasValidImage(props.card);
    
    return (
        <div className={classes.webPlant}>
            <div className={props.card.gotIt ? "cardwrapper_highlight" : "cardwrapper_nohighlight"}>
                <Link to={props.editMode ? "/comicseries/" : "/series/"} onClick={() => props.setCurrentSeries(props.card, props.hero)}>
                    <div className={classes.seriesCard}>
                        <Badge badgeContent={((props.card.ownedOfseries === undefined) ? 0 : props.card.ownedOfseries) + ' / ' + totalAvailable} color="primary" max={9999}>
                            {hasImage ? (
                                <img 
                                    className={classes.seriesImage}
                                    src={getImageUrl(props.card, 'portrait_medium')} 
                                    alt={displaySeriesTitle(props.card.title)} 
                                    title={displaySeriesTitle(props.card.title)}
                                />
                            ) : (
                                <div className={classes.seriesPlaceholder}>
                                    {displaySeriesTitle(props.card.title)}
                                </div>
                            )}
                        </Badge>
                        <div className={classes.seriesTitle}>
                            {props.card.startYear || '?'}-{props.card.endYear || '?'}
                        </div>
                    </div>
                </Link>
            </div>
        </div>
    );
};

export default ComicSeries;